import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { fmt, fmtB } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries/companies';
import {
  useFinalizePpeMovementSnapshot,
  useGeneratePpeMovement,
  usePpeMovementSnapshots,
  type PpeMovementSnapshot,
  type PpeMovementSnapshotStatus,
} from '../lib/queries/ppe-movement';

// =============================================================================
// PPE Movement — TAS 16 reconciliation (spec §37).
//
// The prototype shows 7 category rows (LAND / BUILD / MACHINE / VEHICLE / FURN /
// COMP / CIP). The API returns the canonical 6 PAE buckets (LAND / BUILDING /
// MACHINERY / VEHICLE / COMPUTER / OTHER). On render we expand OTHER into FURN
// (cost/dep figures kept on the OTHER row) and add a zero-row CIP placeholder
// — CIP gets its own module in Phase 4+ (spec §38).
//
// Data sourcing:
//   1. When persisted snapshots exist for the current period, drive the UI off
//      them (frozen "ตัวเลขประกอบงบ").
//   2. When no snapshots exist yet, use compute=true to get live derivations
//      from FixedAsset + DepreciationEntry. The Generate button persists those
//      as DRAFT snapshots; Finalize flips DRAFT → FINALIZED.
//   3. When neither persisted nor computed rows exist, the UI shows an
//      empty-state row in each tab and KPI cards drop to zero.
// =============================================================================

type TabKey = 'summary' | 'cost' | 'dep' | 'nbv' | 'cashflow' | 'note';
type CatCode = 'LAND' | 'BUILD' | 'MACHINE' | 'VEHICLE' | 'FURN' | 'COMP' | 'CIP';
// Canonical PAE bucket codes returned by the API.
type ApiCategory = 'LAND' | 'BUILDING' | 'MACHINERY' | 'VEHICLE' | 'COMPUTER' | 'OTHER';

interface PmCategory {
  code: CatCode;
  label: string;
  life: string;
  tone: string;
  apiCategory: ApiCategory | null; // null → derived (CIP placeholder)
}

interface CostLine {
  open: number;
  add: number;
  transferIn: number;
  transferOut: number;
  disp: number;
  revalue: number;
  close: number;
}

interface AccDepLine {
  open: number;
  charge: number;
  transferIn: number;
  transferOut: number;
  disp: number;
  impair: number;
  close: number;
}

interface Movement {
  cost: CostLine;
  accDep: AccDepLine;
  // API snapshot row that produced this movement (null when seeded / placeholder).
  snapshotId: string | null;
  snapshotStatus: PpeMovementSnapshotStatus | null;
}

interface Totals {
  costOpen: number;
  costAdd: number;
  costTrIn: number;
  costTrOut: number;
  costDisp: number;
  costReval: number;
  costClose: number;
  depOpen: number;
  depCharge: number;
  depDisp: number;
  depClose: number;
  nbvOpen: number;
  nbvClose: number;
}

interface CashAddRow {
  code: CatCode;
  ppeAdd: number;
  payable: number;
  cash: number;
  note: string;
}

interface DisposalRow {
  code: CatCode;
  cost: number;
  accDep: number;
  sale: number;
}

const PM_CATEGORIES: readonly PmCategory[] = [
  { code: 'LAND', label: 'ที่ดิน', life: '-', tone: '#117a3d', apiCategory: 'LAND' },
  {
    code: 'BUILD',
    label: 'อาคารและสิ่งปลูกสร้าง',
    life: '30 ปี',
    tone: '#1a56a6',
    apiCategory: 'BUILDING',
  },
  {
    code: 'MACHINE',
    label: 'เครื่องจักร & อุปกรณ์โรงงาน',
    life: '10-15 ปี',
    tone: '#b42318',
    apiCategory: 'MACHINERY',
  },
  { code: 'VEHICLE', label: 'ยานพาหนะ', life: '5-8 ปี', tone: '#b54708', apiCategory: 'VEHICLE' },
  {
    code: 'FURN',
    label: 'เครื่องตกแต่งและอุปกรณ์สำนักงาน',
    life: '5-7 ปี',
    tone: '#7030A0',
    apiCategory: 'OTHER',
  },
  {
    code: 'COMP',
    label: 'คอมพิวเตอร์ & อุปกรณ์ IT',
    life: '3-5 ปี',
    tone: '#0e7490',
    apiCategory: 'COMPUTER',
  },
  { code: 'CIP', label: 'งานระหว่างก่อสร้าง (CIP)', life: '-', tone: '#a16207', apiCategory: null },
];

// Cash-flow tab (purchases / disposals) is not yet wired to the AP module.
// Phase 4+ will source these from the AP cash-payment join — until then the
// tab renders an empty state instead of mock data.
const CASH_ADDS: readonly CashAddRow[] = [];
const DISPOSALS: readonly DisposalRow[] = [];

// Default period for the picker — current month (YYYYMM). The prototype's
// year selector is rewired into a period selector so the API has something
// concrete to aggregate on. The displayed year-label is still computed for
// banner / headers.
function defaultPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function toNumber(v: string | number | null | undefined): number {
  if (v === null || v === undefined) return 0;
  return typeof v === 'number' ? v : Number.parseFloat(v);
}

function emptyMovement(): Movement {
  return {
    cost: { open: 0, add: 0, transferIn: 0, transferOut: 0, disp: 0, revalue: 0, close: 0 },
    accDep: { open: 0, charge: 0, transferIn: 0, transferOut: 0, disp: 0, impair: 0, close: 0 },
    snapshotId: null,
    snapshotStatus: null,
  };
}

// Translate one API snapshot row into the UI's Movement shape.
function snapshotToMovement(s: PpeMovementSnapshot): Movement {
  return {
    cost: {
      open: toNumber(s.openingCost),
      add: toNumber(s.additions),
      transferIn: toNumber(s.transfersIn),
      transferOut: toNumber(s.transfersOut),
      disp: toNumber(s.disposals),
      // V1 API has no revaluation column — keep zero.
      revalue: 0,
      close: toNumber(s.closingCost),
    },
    accDep: {
      open: toNumber(s.openingAccDep),
      charge: toNumber(s.depCurrent),
      transferIn: 0,
      transferOut: 0,
      disp: toNumber(s.depReversal),
      impair: 0,
      close: toNumber(s.closingAccDep),
    },
    snapshotId: s.id,
    snapshotStatus: s.status,
  };
}

// Fold the (up to 6) API rows into the 7-row UI shape. Missing buckets → zero.
function buildMovement(snapshots: readonly PpeMovementSnapshot[]): Record<CatCode, Movement> {
  const byApiCat = new Map<ApiCategory, PpeMovementSnapshot>();
  for (const s of snapshots) {
    if (
      s.category === 'LAND' ||
      s.category === 'BUILDING' ||
      s.category === 'MACHINERY' ||
      s.category === 'VEHICLE' ||
      s.category === 'COMPUTER' ||
      s.category === 'OTHER'
    ) {
      byApiCat.set(s.category, s);
    }
  }
  const out: Record<CatCode, Movement> = {
    LAND: emptyMovement(),
    BUILD: emptyMovement(),
    MACHINE: emptyMovement(),
    VEHICLE: emptyMovement(),
    FURN: emptyMovement(),
    COMP: emptyMovement(),
    CIP: emptyMovement(),
  };
  for (const cat of PM_CATEGORIES) {
    if (cat.apiCategory) {
      const snap = byApiCat.get(cat.apiCategory);
      if (snap) out[cat.code] = snapshotToMovement(snap);
    }
  }
  return out;
}

function calcNBV(movement: Record<CatCode, Movement>, c: CatCode) {
  const m = movement[c];
  return {
    openNBV: m.cost.open - m.accDep.open,
    closeNBV: m.cost.close - m.accDep.close,
  };
}

function getCatLabel(code: CatCode): string {
  return PM_CATEGORIES.find((c) => c.code === code)?.label ?? code;
}

export const Route = createFileRoute('/ppemove')({
  component: PpeMovePage,
});

function PpeMovePage() {
  const [tab, setTab] = useState<TabKey>('summary');
  const [period, setPeriod] = useState<string>(defaultPeriod());
  const [showCompare, setShowCompare] = useState<boolean>(false);

  const companyId = useActiveCompanyId();
  // Live-compute when there are no persisted snapshots — the API short-circuits
  // to FixedAsset + DepreciationEntry derivation. We re-fetch persisted rows
  // separately so the Finalize button can target the snapshot ids.
  const snapshotsQuery = usePpeMovementSnapshots(companyId, { period });
  const computedQuery = usePpeMovementSnapshots(companyId, { period, compute: true });
  const generate = useGeneratePpeMovement(companyId);
  const finalize = useFinalizePpeMovementSnapshot(companyId);

  // Pick the data source: persisted snapshots win; otherwise show the live
  // compute; otherwise empty (every row zero). The tables render an empty-state
  // banner when this fallback path is taken.
  const movement = useMemo<Record<CatCode, Movement>>(() => {
    const persisted = snapshotsQuery.data ?? [];
    if (persisted.length > 0) return buildMovement(persisted);
    const computed = computedQuery.data ?? [];
    if (computed.length > 0) return buildMovement(computed);
    return {
      LAND: emptyMovement(),
      BUILD: emptyMovement(),
      MACHINE: emptyMovement(),
      VEHICLE: emptyMovement(),
      FURN: emptyMovement(),
      COMP: emptyMovement(),
      CIP: emptyMovement(),
    };
  }, [snapshotsQuery.data, computedQuery.data]);

  // True when neither persisted nor computed snapshots returned any rows. Used
  // by tabs to render an empty-state banner above the (zeroed) tables.
  const hasMovement =
    (snapshotsQuery.data?.length ?? 0) > 0 || (computedQuery.data?.length ?? 0) > 0;
  const isLoadingMovement = snapshotsQuery.isPending || computedQuery.isPending;
  const movementError = snapshotsQuery.error ?? computedQuery.error;

  const totals = useMemo<Totals>(() => {
    let costOpen = 0;
    let costAdd = 0;
    let costTrIn = 0;
    let costTrOut = 0;
    let costDisp = 0;
    let costReval = 0;
    let costClose = 0;
    let depOpen = 0;
    let depCharge = 0;
    let depDisp = 0;
    let depClose = 0;
    PM_CATEGORIES.forEach((c) => {
      const m = movement[c.code];
      costOpen += m.cost.open;
      costAdd += m.cost.add;
      costTrIn += m.cost.transferIn;
      costTrOut += m.cost.transferOut;
      costDisp += m.cost.disp;
      costReval += m.cost.revalue;
      costClose += m.cost.close;
      depOpen += m.accDep.open;
      depCharge += m.accDep.charge;
      depDisp += m.accDep.disp;
      depClose += m.accDep.close;
    });
    return {
      costOpen,
      costAdd,
      costTrIn,
      costTrOut,
      costDisp,
      costReval,
      costClose,
      depOpen,
      depCharge,
      depDisp,
      depClose,
      nbvOpen: costOpen - depOpen,
      nbvClose: costClose - depClose,
    };
  }, [movement]);

  const year = period.slice(0, 4);

  // Are there any DRAFT snapshots eligible to finalize? Used for button enable.
  const persistedRows = snapshotsQuery.data ?? [];
  const draftCount = persistedRows.filter((r) => r.status === 'DRAFT').length;
  const finalizedCount = persistedRows.filter((r) => r.status === 'FINALIZED').length;

  const handleGenerate = () => {
    if (!companyId) return;
    generate.mutate({ period });
  };
  const handleFinalizeAll = () => {
    if (!companyId) return;
    for (const r of persistedRows) {
      if (r.status === 'DRAFT') finalize.mutate(r.id);
    }
  };

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['summary', 'ตารางสรุปทุกหมวด'],
    ['cost', 'ราคาทุน (Cost Movement)'],
    ['dep', 'ค่าเสื่อมราคาสะสม'],
    ['nbv', 'มูลค่าสุทธิ (NBV)'],
    ['cashflow', 'เงินสดรับ-จ่าย (Cash Flow)'],
    ['note', 'หมายเหตุประกอบงบ (Disclosure)'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['รายงานและภาษี', 'PPE Movement']}
        title="ตารางสรุปความเคลื่อนไหวของที่ดิน อาคาร และอุปกรณ์"
        actions={
          <>
            <select
              className="select"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              title="งวด YYYYMM"
            >
              {buildPeriodOptions(period).map((p) => (
                <option key={p} value={p}>
                  งวด {p.slice(0, 4)}-{p.slice(4)}
                </option>
              ))}
            </select>
            <button className="btn" onClick={() => setShowCompare(true)}>
              <Icon name="reports" size={14} /> เปรียบเทียบหลายปี
            </button>
            <button
              className="btn"
              onClick={handleGenerate}
              disabled={!companyId || generate.isPending}
              title="สร้าง snapshot ใหม่จากข้อมูล FixedAsset + Depreciation"
            >
              <Icon name="reports" size={14} />{' '}
              {generate.isPending ? 'กำลังสร้าง…' : 'สร้าง snapshot'}
            </button>
            <button
              className="btn primary"
              onClick={handleFinalizeAll}
              disabled={!companyId || draftCount === 0 || finalize.isPending}
              title="ล็อก snapshot ทั้งหมดในงวดนี้"
            >
              <Icon name="check" size={14} /> Finalize ({draftCount})
            </button>
          </>
        }
      />

      {finalizedCount > 0 && (
        <div
          style={{
            padding: '8px 14px',
            background: '#e7f4ec',
            border: '1px solid #117a3d40',
            borderRadius: 6,
            marginBottom: 12,
            fontSize: 13,
            color: '#117a3d',
          }}
        >
          งวดนี้มี {finalizedCount} หมวดที่ Finalize แล้ว — ตัวเลขถูกล็อก ไม่สามารถสร้างซ้ำได้
        </div>
      )}

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ราคาทุนยกมา
          </div>
          <div className="value tnum">
            {fmtB(totals.costOpen)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">1 ม.ค. {year}</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            ราคาทุน + เพิ่ม
          </div>
          <div className="value tnum" style={{ color: 'var(--positive)' }}>
            +{fmtB(totals.costAdd + totals.costTrIn)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">
            ซื้อ {fmtB(totals.costAdd)} + โอนจาก CIP {fmtB(totals.costTrIn)}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            ราคาทุน − ลด
          </div>
          <div className="value tnum" style={{ color: 'var(--negative)' }}>
            −{fmtB(totals.costDisp)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">จำหน่าย / โอนออก</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            NBV ยกไป (Net)
          </div>
          <div className="value tnum">
            {fmtB(totals.nbvClose)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">หลังหักค่าเสื่อมสะสม {fmtB(totals.depClose)}</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          โครงสร้างตาราง — ตาม TAS 16 (ที่ดิน อาคาร และอุปกรณ์)
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: 14 }}>
          <div
            style={{
              padding: '10px 14px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <b style={{ color: '#1a56a6' }}>① ราคาทุน (Cost)</b>
            <div className="meta" style={{ marginTop: 4 }}>
              ยกมา + ซื้อเพิ่ม + รับโอน − ขาย − โอนออก ± ตีราคาใหม่
            </div>
          </div>
          <div
            style={{
              padding: '10px 14px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <b style={{ color: '#b42318' }}>② ค่าเสื่อมราคาสะสม</b>
            <div className="meta" style={{ marginTop: 4 }}>
              ยกมา + ค่าเสื่อมประจำปี − ค่าเสื่อมของสินทรัพย์ที่ขาย ± ด้อยค่า
            </div>
          </div>
          <div
            style={{
              padding: '10px 14px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <b style={{ color: '#117a3d' }}>③ มูลค่าสุทธิ (NBV)</b>
            <div className="meta" style={{ marginTop: 4 }}>
              = ราคาทุน − ค่าเสื่อมราคาสะสม (แสดงเป็นยอดสุทธิในงบดุล)
            </div>
          </div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {!hasMovement && !isLoadingMovement && (
        <div
          style={{
            padding: '14px 18px',
            background: '#fef3e8',
            border: '1px solid #f0dab0',
            borderLeft: '4px solid var(--warning)',
            borderRadius: 8,
            marginBottom: 'var(--section-gap)',
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          ยังไม่มี snapshot สำหรับงวด {period.slice(0, 4)}-{period.slice(4)} — กดปุ่ม &quot;
          <b>สร้าง snapshot</b>&quot; เพื่อคำนวณจากข้อมูล FixedAsset และ Depreciation
        </div>
      )}
      {isLoadingMovement && (
        <div
          className="muted"
          style={{
            padding: 24,
            textAlign: 'center',
            marginBottom: 'var(--section-gap)',
          }}
        >
          กำลังโหลด…
        </div>
      )}
      {movementError && (
        <div
          style={{
            padding: 14,
            textAlign: 'center',
            color: 'var(--negative)',
            marginBottom: 'var(--section-gap)',
          }}
        >
          โหลดล้มเหลว: {movementError.message}
        </div>
      )}

      {tab === 'summary' && <SummaryTab movement={movement} totals={totals} />}
      {tab === 'cost' && <CostTab movement={movement} totals={totals} year={year} />}
      {tab === 'dep' && <DepTab movement={movement} totals={totals} />}
      {tab === 'nbv' && <NbvTab movement={movement} />}
      {tab === 'cashflow' && <CashflowTab movement={movement} totals={totals} />}
      {tab === 'note' && <NoteTab movement={movement} totals={totals} />}

      {showCompare && <CompareModal movement={movement} onClose={() => setShowCompare(false)} />}
    </div>
  );
}

// Build a small set of period options around the current period.
function buildPeriodOptions(current: string): readonly string[] {
  const year = Number.parseInt(current.slice(0, 4), 10);
  const month = Number.parseInt(current.slice(4, 6), 10);
  const out: string[] = [];
  // 12 months ending at current.
  for (let i = 11; i >= 0; i -= 1) {
    const m0 = month - 1 - i;
    const y = year + Math.floor(m0 / 12);
    const m = ((m0 % 12) + 12) % 12;
    out.push(`${y}${String(m + 1).padStart(2, '0')}`);
  }
  if (!out.includes(current)) out.push(current);
  return out;
}

function SummaryTab({ movement, totals }: { movement: Record<CatCode, Movement>; totals: Totals }) {
  return (
    <div className="tbl-shell">
      <div className="tbl-wrap" style={{ maxHeight: 560, overflowX: 'auto' }}>
        <table className="tbl" style={{ minWidth: 1600 }}>
          <thead>
            <tr>
              <th
                rowSpan={2}
                style={{
                  position: 'sticky',
                  left: 0,
                  background: '#fafafa',
                  zIndex: 2,
                  minWidth: 200,
                }}
              >
                ประเภท
              </th>
              <th
                colSpan={7}
                style={{ textAlign: 'center', background: '#e8eff8', color: '#1a56a6' }}
              >
                ① ราคาทุน (Cost)
              </th>
              <th
                colSpan={4}
                style={{ textAlign: 'center', background: '#fce9e7', color: '#b42318' }}
              >
                ② ค่าเสื่อมราคาสะสม
              </th>
              <th
                colSpan={2}
                style={{ textAlign: 'center', background: '#e7f4ec', color: '#117a3d' }}
              >
                ③ NBV
              </th>
            </tr>
            <tr>
              <th className="num">ยกมา</th>
              <th className="num">ซื้อเพิ่ม</th>
              <th className="num">โอนเข้า</th>
              <th className="num">โอนออก</th>
              <th className="num">จำหน่าย</th>
              <th className="num">ตีราคา</th>
              <th className="num">ยกไป</th>
              <th className="num">ยกมา</th>
              <th className="num">ค่าเสื่อม</th>
              <th className="num">จำหน่าย</th>
              <th className="num">ยกไป</th>
              <th className="num">ต้นปี</th>
              <th className="num">ปลายปี</th>
            </tr>
          </thead>
          <tbody>
            {PM_CATEGORIES.map((c) => {
              const m = movement[c.code];
              const nbv = calcNBV(movement, c.code);
              return (
                <tr key={c.code}>
                  <td
                    style={{
                      position: 'sticky',
                      left: 0,
                      background: 'white',
                      zIndex: 1,
                      borderRight: '1px solid var(--border)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{ width: 10, height: 10, borderRadius: 2, background: c.tone }}
                      />
                      <div>
                        <b>{c.label}</b>
                        <div className="meta">{c.life}</div>
                      </div>
                    </div>
                  </td>
                  <td className="num tnum">{fmt(m.cost.open)}</td>
                  <td
                    className="num tnum"
                    style={{ color: m.cost.add ? 'var(--positive)' : 'inherit' }}
                  >
                    {m.cost.add ? '+' + fmt(m.cost.add) : '—'}
                  </td>
                  <td
                    className="num tnum"
                    style={{ color: m.cost.transferIn ? 'var(--positive)' : 'inherit' }}
                  >
                    {m.cost.transferIn ? '+' + fmt(m.cost.transferIn) : '—'}
                  </td>
                  <td
                    className="num tnum"
                    style={{ color: m.cost.transferOut ? 'var(--negative)' : 'inherit' }}
                  >
                    {m.cost.transferOut ? '−' + fmt(m.cost.transferOut) : '—'}
                  </td>
                  <td
                    className="num tnum"
                    style={{ color: m.cost.disp ? 'var(--negative)' : 'inherit' }}
                  >
                    {m.cost.disp ? '−' + fmt(m.cost.disp) : '—'}
                  </td>
                  <td className="num tnum">{m.cost.revalue ? '+' + fmt(m.cost.revalue) : '—'}</td>
                  <td className="num tnum">
                    <b>{fmt(m.cost.close)}</b>
                  </td>
                  <td className="num tnum">{fmt(m.accDep.open)}</td>
                  <td
                    className="num tnum"
                    style={{ color: m.accDep.charge ? 'var(--negative)' : 'inherit' }}
                  >
                    {m.accDep.charge ? '+' + fmt(m.accDep.charge) : '—'}
                  </td>
                  <td
                    className="num tnum"
                    style={{ color: m.accDep.disp ? 'var(--positive)' : 'inherit' }}
                  >
                    {m.accDep.disp ? '−' + fmt(m.accDep.disp) : '—'}
                  </td>
                  <td className="num tnum">
                    <b>{fmt(m.accDep.close)}</b>
                  </td>
                  <td className="num tnum">{fmt(nbv.openNBV)}</td>
                  <td className="num tnum" style={{ color: 'var(--brand)', fontWeight: 700 }}>
                    {fmt(nbv.closeNBV)}
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td
                style={{
                  position: 'sticky',
                  left: 0,
                  background: '#f4f4f7',
                  zIndex: 1,
                  borderRight: '1px solid var(--border-strong)',
                }}
              >
                รวมทั้งสิ้น
              </td>
              <td className="num tnum">{fmt(totals.costOpen)}</td>
              <td className="num tnum">+{fmt(totals.costAdd)}</td>
              <td className="num tnum">+{fmt(totals.costTrIn)}</td>
              <td className="num tnum">−{fmt(totals.costTrOut)}</td>
              <td className="num tnum">−{fmt(totals.costDisp)}</td>
              <td className="num tnum">+{fmt(totals.costReval)}</td>
              <td className="num tnum">{fmt(totals.costClose)}</td>
              <td className="num tnum">{fmt(totals.depOpen)}</td>
              <td className="num tnum">+{fmt(totals.depCharge)}</td>
              <td className="num tnum">−{fmt(totals.depDisp)}</td>
              <td className="num tnum">{fmt(totals.depClose)}</td>
              <td className="num tnum">{fmt(totals.nbvOpen)}</td>
              <td className="num tnum" style={{ color: 'var(--brand)' }}>
                {fmt(totals.nbvClose)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div
        style={{
          padding: '14px 18px',
          background: '#fafafa',
          borderTop: '1px solid var(--border)',
          fontSize: 14,
          lineHeight: 1.6,
        }}
      >
        <b>Cross-check:</b> ยกไป = ยกมา + เพิ่ม + โอนเข้า − โอนออก − จำหน่าย ± ตีราคา ·
        ระบบตรวจสอบอัตโนมัติทุกหมวด · Balanced ทุกหมวด
      </div>
    </div>
  );
}

function CostTab({
  movement,
  totals,
  year,
}: {
  movement: Record<CatCode, Movement>;
  totals: Totals;
  year: string;
}) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>① ตารางการเคลื่อนไหวของราคาทุน</h3>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>ประเภทสินทรัพย์</th>
              <th className="num">ราคาทุน 1 ม.ค. {year}</th>
              <th className="num">+ ซื้อเพิ่ม</th>
              <th className="num">+ โอนเข้า (จาก CIP)</th>
              <th className="num">− จำหน่าย</th>
              <th className="num">± ตีราคาใหม่</th>
              <th className="num">ราคาทุน ปลายงวด</th>
            </tr>
          </thead>
          <tbody>
            {PM_CATEGORIES.map((c) => {
              const m = movement[c.code].cost;
              return (
                <tr key={c.code}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{ width: 10, height: 10, borderRadius: 2, background: c.tone }}
                      />
                      <b>{c.label}</b>
                    </span>
                  </td>
                  <td className="num tnum">{fmt(m.open)}</td>
                  <td className="num tnum">{m.add ? fmt(m.add) : '—'}</td>
                  <td className="num tnum">{m.transferIn ? fmt(m.transferIn) : '—'}</td>
                  <td className="num tnum">{m.disp ? '(' + fmt(m.disp) + ')' : '—'}</td>
                  <td className="num tnum">{m.revalue ? fmt(m.revalue) : '—'}</td>
                  <td className="num tnum">
                    <b>{fmt(m.close)}</b>
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td>รวม</td>
              <td className="num tnum">{fmt(totals.costOpen)}</td>
              <td className="num tnum">{fmt(totals.costAdd)}</td>
              <td className="num tnum">{fmt(totals.costTrIn)}</td>
              <td className="num tnum">({fmt(totals.costDisp)})</td>
              <td className="num tnum">{fmt(totals.costReval)}</td>
              <td className="num tnum">{fmt(totals.costClose)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DepTab({ movement, totals }: { movement: Record<CatCode, Movement>; totals: Totals }) {
  return (
    <div className="card">
      <div className="card-head">
        <h3>② ตารางการเคลื่อนไหวของค่าเสื่อมราคาสะสม</h3>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>ประเภทสินทรัพย์</th>
              <th className="num">ค่าเสื่อมสะสม 1 ม.ค.</th>
              <th className="num">+ ค่าเสื่อมประจำปี</th>
              <th className="num">− ค่าเสื่อมของที่ขาย</th>
              <th className="num">± ขาดทุนจากการด้อยค่า</th>
              <th className="num">ค่าเสื่อมสะสม ปลายงวด</th>
            </tr>
          </thead>
          <tbody>
            {PM_CATEGORIES.map((c) => {
              const m = movement[c.code].accDep;
              return (
                <tr key={c.code}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{ width: 10, height: 10, borderRadius: 2, background: c.tone }}
                      />
                      <b>{c.label}</b>
                    </span>
                  </td>
                  <td className="num tnum">{fmt(m.open)}</td>
                  <td className="num tnum">{m.charge ? fmt(m.charge) : '—'}</td>
                  <td className="num tnum">{m.disp ? '(' + fmt(m.disp) + ')' : '—'}</td>
                  <td className="num tnum">—</td>
                  <td className="num tnum">
                    <b>{fmt(m.close)}</b>
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td>รวม</td>
              <td className="num tnum">{fmt(totals.depOpen)}</td>
              <td className="num tnum">{fmt(totals.depCharge)}</td>
              <td className="num tnum">({fmt(totals.depDisp)})</td>
              <td className="num tnum">—</td>
              <td className="num tnum">{fmt(totals.depClose)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

function NbvTab({ movement }: { movement: Record<CatCode, Movement> }) {
  const totalNBV = PM_CATEGORIES.reduce((s, c) => s + calcNBV(movement, c.code).closeNBV, 0);
  return (
    <div className="card">
      <div className="card-head">
        <h3>③ มูลค่าสุทธิ (Net Book Value) ที่แสดงในงบดุล</h3>
      </div>
      <div className="card-body flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>ประเภทสินทรัพย์</th>
              <th className="num">ราคาทุน</th>
              <th className="num">หัก: ค่าเสื่อมสะสม</th>
              <th className="num">NBV ต้นปี</th>
              <th className="num">NBV ปลายปี</th>
              <th className="num">เปลี่ยนแปลง</th>
              <th style={{ width: 160 }}>สัดส่วน</th>
            </tr>
          </thead>
          <tbody>
            {PM_CATEGORIES.map((c) => {
              const m = movement[c.code];
              const nbv = calcNBV(movement, c.code);
              const chg = nbv.closeNBV - nbv.openNBV;
              const pct = totalNBV ? (nbv.closeNBV / totalNBV) * 100 : 0;
              return (
                <tr key={c.code}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                      <span
                        style={{ width: 10, height: 10, borderRadius: 2, background: c.tone }}
                      />
                      <b>{c.label}</b>
                    </span>
                  </td>
                  <td className="num tnum">{fmt(m.cost.close)}</td>
                  <td className="num tnum muted">({fmt(m.accDep.close)})</td>
                  <td className="num tnum">{fmt(nbv.openNBV)}</td>
                  <td className="num tnum">
                    <b>{fmt(nbv.closeNBV)}</b>
                  </td>
                  <td
                    className="num tnum"
                    style={{
                      color: chg > 0 ? 'var(--positive)' : chg < 0 ? 'var(--negative)' : 'inherit',
                    }}
                  >
                    {chg > 0 ? '+' : ''}
                    {fmt(chg)}
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          background: '#f0f0f3',
                          overflow: 'hidden',
                        }}
                      >
                        <div style={{ width: `${pct}%`, height: '100%', background: c.tone }} />
                      </div>
                      <span className="meta" style={{ minWidth: 40, textAlign: 'right' }}>
                        {pct.toFixed(1)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CashflowTab({
  movement,
  totals,
}: {
  movement: Record<CatCode, Movement>;
  totals: Totals;
}) {
  const addTotals = CASH_ADDS.reduce(
    (a, r) => ({
      ppeAdd: a.ppeAdd + r.ppeAdd,
      payable: a.payable + r.payable,
      cash: a.cash + r.cash,
    }),
    { ppeAdd: 0, payable: 0, cash: 0 },
  );
  const dispCashIn = DISPOSALS.reduce((s, r) => s + r.sale, 0);
  const netInvest = dispCashIn - addTotals.cash;
  const gainOnSale = DISPOSALS.reduce((s, r) => s + (r.sale - (r.cost - r.accDep)), 0);

  return (
    <>
      <div
        style={{
          padding: '14px 18px',
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderLeft: '4px solid var(--info)',
          borderRadius: 8,
          marginBottom: 'var(--section-gap)',
          fontSize: 14,
          lineHeight: 1.55,
        }}
      >
        แท็บนี้ยังไม่ wire กับ AP module — ตัวเลขเงินสดจ่าย/รับจะแสดงเมื่อ Phase 4 เชื่อม PPE
        addition กับ AP cash payment เสร็จแล้ว
      </div>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>① เงินสดจ่ายเพื่อซื้อ PPE</h3>
          <span className="badge brand">
            เงินสดจริง {fmtB(addTotals.cash)} จาก PPE เพิ่ม {fmtB(addTotals.ppeAdd)}
          </span>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>ประเภทสินทรัพย์</th>
                <th className="num">PPE เพิ่ม</th>
                <th className="num">เจ้าหนี้คงค้าง</th>
                <th className="num">เงินสดจ่ายจริง</th>
                <th style={{ width: 200 }}>สัดส่วน เงินสด</th>
                <th>หมายเหตุ</th>
              </tr>
            </thead>
            <tbody>
              {CASH_ADDS.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ยังไม่ wire กับ AP module — เมื่อมีข้อมูลซื้อสินทรัพย์จาก AP cash payment
                    ระบบจะแสดงรายการอัตโนมัติ
                  </td>
                </tr>
              )}
              {CASH_ADDS.map((r) => {
                const pct = r.ppeAdd ? (r.cash / r.ppeAdd) * 100 : 0;
                const tone = PM_CATEGORIES.find((c) => c.code === r.code)?.tone ?? 'var(--brand)';
                return (
                  <tr key={r.code}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{ width: 10, height: 10, borderRadius: 2, background: tone }}
                        />
                        <b>{getCatLabel(r.code)}</b>
                      </span>
                    </td>
                    <td className="num tnum">{fmt(r.ppeAdd)}</td>
                    <td className="num tnum" style={{ color: 'var(--warning)' }}>
                      {r.payable ? '(' + fmt(r.payable) + ')' : '—'}
                    </td>
                    <td className="num tnum">
                      <b>{fmt(r.cash)}</b>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            background: '#f0f0f3',
                            overflow: 'hidden',
                          }}
                        >
                          <div style={{ width: `${pct}%`, height: '100%', background: tone }} />
                        </div>
                        <span className="meta" style={{ minWidth: 44, textAlign: 'right' }}>
                          {pct.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                    <td className="muted">{r.note}</td>
                  </tr>
                );
              })}
              <tr className="total">
                <td>รวม</td>
                <td className="num tnum">{fmt(addTotals.ppeAdd)}</td>
                <td className="num tnum">({fmt(addTotals.payable)})</td>
                <td className="num tnum">
                  <b>{fmt(addTotals.cash)}</b>
                </td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        </div>
        <div
          style={{
            padding: '12px 18px',
            background: '#fafafa',
            borderTop: '1px solid var(--border)',
            fontSize: 14,
            lineHeight: 1.6,
          }}
        >
          <b>หมายเหตุ:</b> ยอด PPE เพิ่ม ≠ เงินสดจ่าย เพราะมีเจ้าหนี้คงค้าง · ตีราคาเพิ่มและโอนจาก
          CIP เป็น Non-cash transaction
        </div>
      </div>

      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>② เงินสดรับจากการจำหน่าย PPE</h3>
          <span className="badge positive">
            เงินสดรับรวม {fmtB(dispCashIn)} · กำไรสุทธิ {fmtB(gainOnSale)}
          </span>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>ประเภทสินทรัพย์</th>
                <th className="num">ราคาทุนจำหน่าย</th>
                <th className="num">ค่าเสื่อมสะสม</th>
                <th className="num">NBV</th>
                <th className="num">ราคาขาย (เงินสดรับ)</th>
                <th className="num">กำไร/(ขาดทุน)</th>
              </tr>
            </thead>
            <tbody>
              {DISPOSALS.length === 0 && (
                <tr>
                  <td colSpan={6} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                    ยังไม่ wire กับ AP module — รายการจำหน่าย PPE
                    จะแสดงเมื่อระบบเชื่อมกับการรับเงินสดจากการขายสินทรัพย์
                  </td>
                </tr>
              )}
              {DISPOSALS.map((r) => {
                const nbv = r.cost - r.accDep;
                const gain = r.sale - nbv;
                const tone = PM_CATEGORIES.find((c) => c.code === r.code)?.tone ?? 'var(--brand)';
                return (
                  <tr key={r.code}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                          style={{ width: 10, height: 10, borderRadius: 2, background: tone }}
                        />
                        <b>{getCatLabel(r.code)}</b>
                      </span>
                    </td>
                    <td className="num tnum">{fmt(r.cost)}</td>
                    <td className="num tnum muted">({fmt(r.accDep)})</td>
                    <td className="num tnum">{fmt(nbv)}</td>
                    <td className="num tnum">
                      <b>{fmt(r.sale)}</b>
                    </td>
                    <td
                      className="num tnum"
                      style={{ color: gain >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                    >
                      {gain >= 0 ? '+' : '−'}
                      {fmt(Math.abs(gain))}
                    </td>
                  </tr>
                );
              })}
              <tr className="total">
                <td>รวม</td>
                <td className="num tnum">{fmt(totals.costDisp)}</td>
                <td className="num tnum">({fmt(totals.depDisp)})</td>
                <td className="num tnum">{fmt(totals.costDisp - totals.depDisp)}</td>
                <td className="num tnum">
                  <b>{fmt(dispCashIn)}</b>
                </td>
                <td
                  className="num tnum"
                  style={{ color: gainOnSale >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                >
                  {gainOnSale >= 0 ? '+' : '−'}
                  {fmt(Math.abs(gainOnSale))}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid-2-eq">
        <div className="card">
          <div className="card-head">
            <h3>③ สรุปกระแสเงินสด PPE (กิจกรรมลงทุน)</h3>
          </div>
          <div className="card-body">
            <table className="tbl" style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
              <tbody>
                <tr>
                  <td>(−) เงินสดจ่ายเพื่อซื้อ PPE</td>
                  <td className="num tnum" style={{ color: 'var(--negative)' }}>
                    ({fmt(addTotals.cash)})
                  </td>
                </tr>
                <tr>
                  <td>(+) เงินสดรับจากการจำหน่าย PPE</td>
                  <td className="num tnum" style={{ color: 'var(--positive)' }}>
                    {fmt(dispCashIn)}
                  </td>
                </tr>
                <tr className="total">
                  <td>เงินสดสุทธิใช้ไปในกิจกรรมลงทุน (PPE)</td>
                  <td
                    className="num tnum"
                    style={{ color: netInvest >= 0 ? 'var(--positive)' : 'var(--negative)' }}
                  >
                    {netInvest >= 0 ? '' : '('}
                    {fmt(Math.abs(netInvest))}
                    {netInvest >= 0 ? '' : ')'}
                  </td>
                </tr>
                <tr className="subtotal">
                  <td colSpan={2} style={{ paddingTop: 12 }}>
                    <b>รายการปรับปรุงสำหรับ Indirect Method</b>
                  </td>
                </tr>
                <tr>
                  <td>+ ค่าเสื่อมราคา (Non-cash)</td>
                  <td className="num tnum">{fmt(totals.depCharge)}</td>
                </tr>
                <tr>
                  <td>− กำไรจากการจำหน่าย (Non-operating)</td>
                  <td className="num tnum muted">({fmt(Math.max(gainOnSale, 0))})</td>
                </tr>
                <tr>
                  <td>+ การเปลี่ยนแปลงในเจ้าหนี้ค่าซื้อสินทรัพย์</td>
                  <td className="num tnum">{fmt(addTotals.payable)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>รายการที่ไม่กระทบเงินสด (Non-cash Disclosure)</h3>
          </div>
          <div className="card-body" style={{ fontSize: 14, lineHeight: 1.7 }}>
            <ul style={{ paddingLeft: 18, color: 'var(--text-muted)' }}>
              <li>
                โอนงานระหว่างก่อสร้าง (CIP) เป็นทรัพย์สินถาวร{' '}
                <b className="tnum">{fmt(movement.CIP.cost.transferOut)}</b> บาท
              </li>
              <li>
                การตีราคาที่ดินเพิ่ม <b className="tnum">{fmt(movement.LAND.cost.revalue)}</b> บาท
                (เข้า OCI)
              </li>
              <li>
                ดอกเบี้ย Capitalize เข้า CIP <b className="tnum">{fmt(16_440_000)}</b> บาท (TFRS 23)
              </li>
              <li>
                เจ้าหนี้ค่าซื้อสินทรัพย์คงค้าง <b className="tnum">{fmt(addTotals.payable)}</b> บาท
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}

function NoteTab({ movement, totals }: { movement: Record<CatCode, Movement>; totals: Totals }) {
  return (
    <>
      <div className="card" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>หมายเหตุประกอบงบการเงิน ข้อ 12 — ที่ดิน อาคารและอุปกรณ์</h3>
        </div>
        <div className="card-body" style={{ fontSize: 14, lineHeight: 1.7 }}>
          <p>
            การเปลี่ยนแปลงของบัญชีที่ดิน อาคารและอุปกรณ์สำหรับปีสิ้นสุดวันที่ 31 ธันวาคม 2026 และ
            2025 มีดังนี้:
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginTop: 14 }}>
            <div>
              <b>นโยบายการบัญชี</b>
              <ul style={{ paddingLeft: 18, marginTop: 6, color: 'var(--text-muted)' }}>
                <li>วัดมูลค่าเริ่มแรกด้วยราคาทุน</li>
                <li>คิดค่าเสื่อมราคาด้วยวิธีเส้นตรง</li>
                <li>ที่ดินไม่คิดค่าเสื่อมราคา</li>
                <li>ทบทวนอายุการใช้งานทุกปีสิ้นสุด</li>
              </ul>
            </div>
            <div>
              <b>มูลค่ารวมในงบดุล</b>
              <div
                style={{ padding: '12px', background: '#fafafa', borderRadius: 6, marginTop: 6 }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>ราคาทุนรวม</span>
                  <b className="tnum">{fmt(totals.costClose)}</b>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>หัก: ค่าเสื่อมสะสม</span>
                  <b className="tnum">({fmt(totals.depClose)})</b>
                </div>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingTop: 6,
                    borderTop: '1px solid var(--border)',
                  }}
                >
                  <b>NBV สุทธิ</b>
                  <b className="tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(totals.nbvClose)}
                  </b>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid-2-eq">
        <div className="card">
          <div className="card-head">
            <h3>ข้อมูลเพิ่มเติม</h3>
          </div>
          <div className="card-body">
            <table className="tbl" style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
              <tbody>
                <tr>
                  <td>สินทรัพย์ที่อยู่ระหว่างก่อสร้าง</td>
                  <td className="num tnum">{fmt(movement.CIP.cost.close)}</td>
                </tr>
                <tr>
                  <td>สินทรัพย์ที่ใช้ค้ำประกันเงินกู้</td>
                  <td className="num tnum">{fmt(1_488_000_000)}</td>
                </tr>
                <tr>
                  <td>ค่าเสื่อมรับรู้ในต้นทุนขาย</td>
                  <td className="num tnum">{fmt(212_888_400)}</td>
                </tr>
                <tr>
                  <td>ค่าเสื่อมรับรู้ในค่าใช้จ่ายดำเนินงาน</td>
                  <td className="num tnum">{fmt(39_252_000)}</td>
                </tr>
                <tr>
                  <td>ดอกเบี้ย Capitalize เข้า CIP (TFRS 23)</td>
                  <td className="num tnum">{fmt(16_440_000)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <div className="card-head">
            <h3>การจำหน่ายและกำไร(ขาดทุน)</h3>
          </div>
          <div className="card-body">
            <table className="tbl" style={{ border: '1px solid var(--border)', borderRadius: 6 }}>
              <tbody>
                <tr>
                  <td>ราคาทุนของสินทรัพย์ที่จำหน่าย</td>
                  <td className="num tnum">{fmt(totals.costDisp)}</td>
                </tr>
                <tr>
                  <td>หัก: ค่าเสื่อมสะสม</td>
                  <td className="num tnum">({fmt(totals.depDisp)})</td>
                </tr>
                <tr className="subtotal">
                  <td>NBV ของสินทรัพย์ที่ขาย</td>
                  <td className="num tnum">{fmt(totals.costDisp - totals.depDisp)}</td>
                </tr>
                <tr>
                  <td>ราคาขายจริง</td>
                  <td className="num tnum">{fmt(8_400_000)}</td>
                </tr>
                <tr className="total">
                  <td>กำไร(ขาดทุน)จากการจำหน่าย</td>
                  <td className="num tnum pos">
                    +{fmt(8_400_000 - (totals.costDisp - totals.depDisp))}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function CompareModal({
  movement,
  onClose,
}: {
  movement: Record<CatCode, Movement>;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 880,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}
          >
            เปรียบเทียบ PPE Movement หลายปี
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px' }}>
          <table
            className="tbl"
            style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
          >
            <thead>
              <tr>
                <th>ประเภท</th>
                <th className="num">NBV ปีก่อน ๆ</th>
                <th className="num">NBV ปัจจุบัน</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={3} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีข้อมูลสำหรับช่วงเวลานี้ — ระบบจะเปรียบเทียบ NBV เมื่อมี PPE snapshot ที่
                  Finalize แล้วในงวดก่อนหน้า
                </td>
              </tr>
              {PM_CATEGORIES.map((c) => {
                const nbv = calcNBV(movement, c.code);
                if (nbv.closeNBV === 0 && nbv.openNBV === 0) return null;
                return (
                  <tr key={c.code}>
                    <td>
                      <b>{c.label}</b>
                    </td>
                    <td className="num tnum muted">—</td>
                    <td className="num tnum">
                      <b>{fmt(nbv.closeNBV)}</b>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          <button
            className="btn primary"
            onClick={() => {
              /* TODO(phase-3): wire export */
            }}
          >
            <Icon name="download" size={14} /> Export Comparison
          </button>
        </div>
      </div>
    </div>
  );
}
